import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./Button";
import { EmptyState } from "./display";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Mudou (ex.: a rota), a tela tenta de novo. */
  resetKey?: unknown;
  /** Avisa o envio de erros (GlitchTip). */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Tela própria; padrão: aviso com "Tentar de novo" e "Recarregar". */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
  key: unknown;
}

/**
 * Segura a falha de uma tela para não deixar a página em branco: o resto do app (menu, topo) continua funcionando e a
 * pessoa pode tentar de novo. Use em volta das rotas, com resetKey = caminho atual.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: State): Partial<State> | null {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div role="alert">
        <EmptyState
          icon={AlertTriangle}
          title="Algo deu errado nesta tela"
          actions={
            <>
              <Button variant="primary" onClick={this.reset}>
                Tentar de novo
              </Button>
              <Button onClick={() => window.location.reload()}>Recarregar a página</Button>
            </>
          }
        >
          A falha foi registrada para a equipe. Seus dados salvos não foram afetados; se acontecer de novo, recarregue a
          página ou volte mais tarde.
        </EmptyState>
      </div>
    );
  }
}
